/**
 * Helpers de formatação WhatsApp para mensagens de follow-up.
 */

/** Converte HTML legado (editor antigo) para texto WhatsApp. */
export function legacyHtmlToWhatsAppText(html: string): string {
    if (!html || typeof html !== 'string') return '';
    const trimmed = html.trim();
    if (!/<[a-z][\s\S]*>/i.test(trimmed)) return trimmed;

    let s = trimmed;
    s = s.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_m, inner: string) => {
        let n = 0;
        return `\n${inner.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (_li, item: string) => {
            n += 1;
            return `${n}. ${stripLegacyInline(item).trim()}\n`;
        })}`;
    });
    s = s.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (_m, inner: string) =>
        `\n${inner.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (_li, item: string) => `• ${stripLegacyInline(item).trim()}\n`)}`
    );
    s = s.replace(/<\/(p|div|h[1-6]|blockquote)>/gi, '\n');
    s = s.replace(/<br\s*\/?>/gi, '\n');
    s = s.replace(/<(strong|b)(\s[^>]*)?>([\s\S]*?)<\/\1>/gi, '*$3*');
    s = s.replace(/<(em|i)(\s[^>]*)?>([\s\S]*?)<\/\1>/gi, '_$3_');
    s = s.replace(/<(s|del|strike)(\s[^>]*)?>([\s\S]*?)<\/\1>/gi, '~$3~');
    s = s.replace(/<\/?u(\s[^>]*)?>/gi, '');
    s = s.replace(/<[^>]+>/g, '');
    s = s
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/&#x27;/gi, "'");
    return s.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

function stripLegacyInline(fragment: string): string {
    return legacyHtmlToWhatsAppText(fragment);
}

export type WhatsAppFormat = 'bold' | 'italic' | 'strike' | 'bulletList' | 'orderedList' | 'clear';

export const WHATSAPP_WRAP: Record<'bold' | 'italic' | 'strike', { open: string; close: string }> = {
    bold: { open: '*', close: '*' },
    italic: { open: '_', close: '_' },
    strike: { open: '~', close: '~' },
};

export function wrapSelection(
    text: string,
    start: number,
    end: number,
    open: string,
    close: string,
): { next: string; selectionStart: number; selectionEnd: number } {
    const selected = text.slice(start, end);
    if (selected.startsWith(open) && selected.endsWith(close) && selected.length >= open.length + close.length) {
        const unwrapped = selected.slice(open.length, selected.length - close.length);
        return {
            next: text.slice(0, start) + unwrapped + text.slice(end),
            selectionStart: start,
            selectionEnd: start + unwrapped.length,
        };
    }
    const wrapped = `${open}${selected || 'texto'}${close}`;
    return {
        next: text.slice(0, start) + wrapped + text.slice(end),
        selectionStart: start + open.length,
        selectionEnd: start + open.length + (selected || 'texto').length,
    };
}

export function prefixSelectedLines(
    text: string,
    start: number,
    end: number,
    mode: 'bullet' | 'ordered',
): { next: string; selectionStart: number; selectionEnd: number } {
    const lineStart = text.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
    const lineEndIdx = text.indexOf('\n', end);
    const lineEnd = lineEndIdx === -1 ? text.length : lineEndIdx;
    const block = text.slice(lineStart, lineEnd);
    const lines = block.split('\n');
    let n = 0;
    const rewritten = lines.map((line) => {
        const bare = line.replace(/^(?:[•\-*]\s+|\d+\.\s+)/, '');
        if (mode === 'bullet') return `• ${bare}`;
        n += 1;
        return `${n}. ${bare}`;
    }).join('\n');
    return {
        next: text.slice(0, lineStart) + rewritten + text.slice(lineEnd),
        selectionStart: lineStart,
        selectionEnd: lineStart + rewritten.length,
    };
}

export function clearWhatsAppMarkers(
    text: string,
    start: number,
    end: number,
): { next: string; selectionStart: number; selectionEnd: number } {
    const rangeStart = start === end ? 0 : start;
    const rangeEnd = start === end ? text.length : end;
    const selected = text.slice(rangeStart, rangeEnd);
    const cleaned = selected
        .replace(/\*([^*\n]+)\*/g, '$1')
        .replace(/_([^_\n]+)_/g, '$1')
        .replace(/~([^~\n]+)~/g, '$1')
        .replace(/```([^`]+)```/g, '$1')
        .replace(/^(?:[•\-*]\s+|\d+\.\s+)/gm, '');
    return {
        next: text.slice(0, rangeStart) + cleaned + text.slice(rangeEnd),
        selectionStart: rangeStart,
        selectionEnd: rangeStart + cleaned.length,
    };
}
